import { Navigate } from 'react-router-dom';

import { iaRa } from 'lib/helpers/utility';
import { withLazyFallback } from 'lib/hoc/withLazyFallback';

import ViewportRedirect from '../ViewportRedirect';
import PRACTICE_COMMS_CENTER from '../../practice/comms-center';
import PRACTICE_BEYOND_BILLING from '../../practice/beyond-billing';
import { PATIENT_CHART_CHILDREN } from '../../practice/charts/charts';

import VIEWPORT_SETTINGS from './viewportSettings';

const NotFound = withLazyFallback(() => import('components/NotFound'));
const AdminPracticesOrganization = withLazyFallback(
  () => import('components/admin/pages/Practices/Organization/Organization')
);
const PracticeImportRequests = withLazyFallback(
  () => import('components/admin/pages/Practices/PatientImport/PracticeImportRequests')
);
const Overview = withLazyFallback(
  () =>
    import('components/admin/pages/Practices/Viewport/components/PracticeViewport/pages/Overview')
);
const ViewportCalendar = withLazyFallback(
  () =>
    import('components/admin/pages/Practices/Viewport/components/PracticeViewport/pages/Calendar')
);
const ViewportCharts = withLazyFallback(
  () => import('components/admin/pages/Practices/Viewport/components/PracticeViewport/pages/Charts')
);
const PatientViewport = withLazyFallback(
  () =>
    import('components/admin/pages/Practices/Viewport/components/PatientViewport/PatientViewport')
);
const PracticeSwitch = withLazyFallback(
  () =>
    import('components/admin/pages/Practices/Viewport/components/PracticeViewport/pages/PracticeSwitch')
);
const ViewportDocuments = withLazyFallback(
  () =>
    import('components/admin/pages/Practices/Viewport/components/PracticeViewport/pages/Documents')
);
const PracticeIQueue = withLazyFallback(() => import('components/practice/PracticeIQueue'));
const PracticeIQueueOverview = withLazyFallback(
  () => import('components/practice/PracticeIQueue/Overview/Overview')
);
const PracticeIQueueTasks = withLazyFallback(
  () => import('components/practice/PracticeIQueue/Tasks/Tasks')
);
const PracticeIQueueViewTask = withLazyFallback(
  () => import('components/practice/PracticeIQueue/Tasks/components/ViewTask')
);
const PracticeIQueueClinicalNotes = withLazyFallback(
  () => import('components/practice/PracticeIQueue/ClinicalNotes/ClinicalNotes')
);
const PracticeIQueueTreatmentPlans = withLazyFallback(
  () => import('components/practice/PracticeIQueue/TreatmentPlan')
);
const PracticeIQueueNotifications = withLazyFallback(
  () => import('components/practice/PracticeIQueue/Notifications/Notifications')
);
const PracticeIQueueAppointments = withLazyFallback(
  () => import('components/practice/PracticeIQueue/Appointments/AppointmentsQueue')
);
const PracticeIQueueForms = withLazyFallback(
  () => import('components/practice/PracticeIQueue/FormPacket/FormPacket')
);
const PracticeIQueueOrders = withLazyFallback(
  () => import('components/practice/PracticeIQueue/Orders/Orders')
);

const VIEWPORT_PATIENT_CHART_CHILDREN = PATIENT_CHART_CHILDREN.filter(
  (child) => child.path !== 'eprescribe'
).map((child) =>
  child.path === 'orders'
    ? {
        ...child,
        children: iaRa(child.children).filter((order) => order?.path !== 'prescriptions')
      }
    : child
);

const VIEWPORT_CHILDREN = [
  { index: true, element: <Navigate to="dashboard" replace /> },
  { path: 'dashboard', element: <Overview />, handle: { title: 'Dashboard' } },
  { path: 'appointments', element: <ViewportCalendar />, handle: { title: 'Calendar' } },
  {
    path: 'charts',
    element: <ViewportCharts />,
    handle: { title: 'Charts' },
    children: [
      { path: ':id', element: <PatientViewport />, children: VIEWPORT_PATIENT_CHART_CHILDREN }
    ]
  },
  ...PRACTICE_BEYOND_BILLING,
  ...PRACTICE_COMMS_CENTER,
  { path: 'documents', element: <ViewportDocuments />, handle: { title: 'Documents' } },
  { path: 'documents/:pathId', element: <ViewportDocuments />, handle: { title: 'Documents' } },
  {
    path: 'practice-i-queue',
    element: <PracticeIQueue />,
    handle: { title: 'PracticeIQueue' },
    children: [
      { path: '', element: <PracticeIQueueOverview />, handle: { title: 'Overview' } },
      {
        path: 'tasks',
        element: <PracticeIQueueTasks />,
        handle: { title: 'Tasks' },
        children: [
          { path: ':id', element: <PracticeIQueueViewTask /> },
          { path: '*', element: <NotFound /> }
        ]
      },
      {
        path: 'clinical-notes',
        element: <PracticeIQueueClinicalNotes />,
        handle: { title: 'Clinical notes' }
      },
      {
        path: 'treatment-plans',
        element: <PracticeIQueueTreatmentPlans />,
        handle: { title: 'Treatment plans' }
      },
      {
        path: 'notifications',
        element: <PracticeIQueueNotifications />,
        handle: { title: 'Notifications' }
      },
      {
        path: 'appointments',
        element: <PracticeIQueueAppointments />,
        handle: { title: 'Appointments' }
      },
      { path: 'forms', element: <PracticeIQueueForms />, handle: { title: 'Forms' } },
      {
        path: 'orders',
        element: <PracticeIQueueOrders fromPatient={false} />,
        handle: { title: 'Orders' }
      }
    ]
  },
  ...VIEWPORT_SETTINGS,
  {
    path: 'admin-tools/organization',
    element: <AdminPracticesOrganization />,
    handle: { title: 'Organization' }
  },
  {
    path: 'admin-tools/patient-import',
    element: <PracticeImportRequests />,
    handle: { title: 'Patient import' }
  },
  { path: 'switching', element: <PracticeSwitch /> },
  { path: 'viewport/*', element: <ViewportRedirect /> },
  { path: '*', element: <NotFound /> }
];

export default VIEWPORT_CHILDREN;
